/* Lookbook — Symmetry (Salt Yard) / D'S DAMAT
   Uses the theme's cart plumbing: theme.routes.cartAdd, section rendering for
   page-header + cart-drawer (updateFromCartChange), .js-cart-drawer.open(),
   theme.settings.afterAddToCart, on:cart:change, window.trapFocus. */

if (!customElements.get('lookbook-section')) {
  class LookbookSection extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.init();
    }

    init() {
      const dataNode = this.querySelector('[data-lookbook-data]');
      if (!dataNode) return;

      try {
        this.data = JSON.parse(dataNode.textContent);
      } catch (e) {
        return;
      }
      if (!this.data.looks || !this.data.looks.length) return;

      this.strings = this.data.strings || {};
      this.modal = this.querySelector('[data-lookbook-modal]');
      this.dialog = this.querySelector('.lookbook__dialog');
      this.icons = {};

      const iconTpl = this.querySelector('[data-lookbook-icons]');
      if (iconTpl) {
        iconTpl.content.querySelectorAll('[data-icon]').forEach(node => {
          this.icons[node.dataset.icon] = node.innerHTML;
        });
      }

      this.formatter = new Intl.NumberFormat(this.data.locale || 'en', {
        style: 'currency',
        currency: this.data.currency || 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      this.current = -1;
      this.openRow = -1;
      this.preview = null;
      this.previewCache = {};
      this.warnRow = -1;
      this.slide = 0;
      this.picked = {};
      this.inCart = {};

      // Move the modal to body: section-level transforms/animations would
      // otherwise break fixed positioning and the page blur.
      this.modal.style.setProperty(
        '--lookbook-ratio',
        getComputedStyle(this).getPropertyValue('--lookbook-ratio').trim() || '3/4'
      );
      document.body.appendChild(this.modal);

      this.boundClick = this.onClick.bind(this);
      this.addEventListener('click', this.boundClick);
      this.modal.addEventListener('click', this.boundClick);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPopstate = this.onPopstate.bind(this);
      this.onResize = this.onResize.bind(this);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopstate);
      window.addEventListener('resize', this.onResize);

      this.openFromUrl();
    }

    /* ---------- helpers ---------- */

    get drawer() {
      return document.querySelector('.js-cart-drawer');
    }

    get themeSettings() {
      return (window.theme && window.theme.settings) || {};
    }

    money(cents) {
      return this.formatter.format(cents / 100);
    }

    esc(value) {
      return String(value == null ? '' : value).replace(/[&<>"']/g, c => {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    stageRatioRaw() {
      // --lookbook-ratio from the section (e.g. "3/4"); brand default as fallback
      const raw = getComputedStyle(this).getPropertyValue('--lookbook-ratio').trim();
      if (/^\d+(\.\d+)?\s*\/\s*\d+(\.\d+)?$/.test(raw)) return raw;
      return '3/4';
    }

    stageRatio() {
      const parts = this.stageRatioRaw().split('/').map(parseFloat);
      return parts[0] / parts[1];
    }

    syncLayout() {
      if (window.innerWidth <= 749) return; // mobile: centred sheet handled by CSS

      const header = document.querySelector('.section-header, #pageheader');
      let offset = 0;
      if (header) {
        const rect = header.getBoundingClientRect();
        // sticky header may be partially scrolled away; count the visible part only
        if (rect.height > 0) {
          offset = Math.min(Math.max(rect.bottom, 0), window.innerHeight * 0.4);
        }
      }

      const narrow = window.innerWidth <= 989;
      const gap = narrow ? 20 : 30;
      const maxW = window.innerWidth - (narrow ? 24 : 60);
      const availH = Math.max(window.innerHeight - offset - gap * 2, 320);
      const ratio = this.stageRatio();

      // Panel: readable fixed range; remaining width goes to the image
      const panelW = Math.min(Math.max(window.innerWidth * 0.36, 380), 500);

      let h = availH;
      let stageW = h * ratio;
      if (stageW + panelW > maxW) {
        stageW = Math.max(maxW - panelW, 280);
        h = stageW / ratio; // ratio is never distorted, the popup shrinks instead
      }

      const top = offset + gap + Math.max(0, (availH - h) / 2);

      const s = this.modal.style;
      s.setProperty('--lookbook-header-offset', `${Math.round(offset)}px`);
      s.setProperty('--lookbook-top', `${Math.round(top)}px`);
      s.setProperty('--lookbook-h', `${Math.round(h)}px`);
      s.setProperty('--lookbook-stage-w', `${Math.round(stageW)}px`);
      s.setProperty('--lookbook-w', `${Math.round(stageW + panelW)}px`);
    }

    onResize() {
      if (this.current !== -1) this.syncLayout();
    }

    share() {
      const look = this.data.looks[this.current];
      const url = window.location.href;
      const payload = { title: `${look.title || this.strings.look + ' ' + look.no} — ${this.data.shopName || ''}`.trim(), url };

      if (navigator.share) {
        navigator.share(payload).catch(() => {});
        return;
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(url)
          .then(() => this.say(this.strings.linkCopied))
          .catch(() => {});
      }
    }

    openZoom(productIndex, imageIndex) {
      const look = this.data.looks[this.current];
      const gal = (look.products[productIndex] || {}).gallery || [];
      if (!gal.length) return;

      this.zoomItems = gal;
      this.zoomIdx = Math.min(Math.max(imageIndex, 0), gal.length - 1);

      let box = this.dialog.querySelector('[data-lookbook-zoombox]');
      if (!box) {
        box = document.createElement('div');
        box.className = 'lookbook__zoom';
        box.setAttribute('data-lookbook-zoombox', '');
        box.innerHTML =
          `<button type="button" class="lookbook__zoomclose" data-lookbook-zoomclose aria-label="${this.esc(this.strings.close)}">${this.icons.close || '\u2715'}</button>` +
          `<button type="button" class="lookbook__zoomnav lookbook__zoomnav--prev" data-lookbook-zoomstep="-1" aria-label="${this.esc(this.strings.previous)}">${this.icons.prev || '\u2039'}</button>` +
          '<img alt="">' +
          `<button type="button" class="lookbook__zoomnav lookbook__zoomnav--next" data-lookbook-zoomstep="1" aria-label="${this.esc(this.strings.next)}">${this.icons.next || '\u203a'}</button>`;
        this.dialog.appendChild(box);
      }
      box.hidden = false;
      this.updateZoom();
    }

    updateZoom() {
      const box = this.dialog.querySelector('[data-lookbook-zoombox]');
      if (!box) return;
      box.querySelector('img').src = this.zoomItems[this.zoomIdx].full;
      const multi = this.zoomItems.length > 1;
      box.querySelectorAll('[data-lookbook-zoomstep]').forEach(b => {
        b.style.visibility = multi ? 'visible' : 'hidden';
      });
    }

    stepZoom(delta) {
      const len = this.zoomItems.length;
      this.zoomIdx = (this.zoomIdx + delta + len) % len;
      this.updateZoom();
    }

    closeZoom() {
      const box = this.dialog.querySelector('[data-lookbook-zoombox]');
      if (box) box.hidden = true;
    }

    zoomOpen() {
      const box = this.dialog && this.dialog.querySelector('[data-lookbook-zoombox]');
      return !!(box && !box.hidden);
    }

    previewFinal() {
      return this.preview && this.preview.final < this.preview.orig ? this.preview.final : null;
    }

    cartPreviewVariant(product) {
      return product.variants.find(x => x.available) || product.variants[0];
    }

    loadCartPrices() {
      // Builds an invisible preview cart via the Storefront API and reads the real
      // line prices back, so automatic discounts / BXGY rules are always correct.
      // Never touches the customer's real cart. Disabled when no token is set.
      if (!this.data.sfToken) return;

      const look = this.data.looks[this.current];
      const ids = look.products.map(p => {
        const v = this.cartPreviewVariant(p);
        return v ? v.id : null;
      });
      if (!ids.length || ids.some(id => !id)) return;

      const key = ids.join(',');
      this.previewKey = key;

      if (this.previewCache[key]) {
        this.preview = this.previewCache[key];
        this.render();
        return;
      }

      const lines = ids
        .map(id => `{ merchandiseId: "gid://shopify/ProductVariant/${id}", quantity: 1 }`)
        .join(', ');
      const query = `mutation { cartCreate(input: { lines: [${lines}] }) { cart { lines(first: 50) { nodes { merchandise { ... on ProductVariant { id } } cost { subtotalAmount { amount } totalAmount { amount } } } } } userErrors { message } } }`;

      fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.data.sfToken
        },
        body: JSON.stringify({ query })
      })
        .then(r => r.json())
        .then(json => {
          const cart = json && json.data && json.data.cartCreate && json.data.cartCreate.cart;
          if (!cart || !cart.lines) return;

          const byVariant = {};
          let orig = 0;
          let final = 0;
          cart.lines.nodes.forEach(n => {
            const vid = parseInt((n.merchandise.id || '').split('/').pop(), 10);
            const o = Math.round(parseFloat(n.cost.subtotalAmount.amount) * 100);
            const f = Math.round(parseFloat(n.cost.totalAmount.amount) * 100);
            byVariant[vid] = { orig: o, final: f };
            orig += o;
            final += f;
          });

          const preview = { key, byVariant, orig, final };
          this.previewCache[key] = preview;
          if (this.current !== -1 && this.previewKey === key) {
            this.preview = preview;
            this.render();
          }
        })
        .catch(() => {});
    }

    say(text, warn) {
      const el = this.dialog.querySelector('[data-lookbook-msg]');
      if (!el) return;
      el.textContent = text || '';
      el.classList.toggle('lookbook__msg--warn', !!warn);
    }

    scrollToRow(index) {
      const row = this.dialog.querySelector(`[data-lookbook-row="${index}"]`);
      if (row && row.scrollIntoView) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    clearWarns() {
      this.warnRow = -1;
      this.dialog.querySelectorAll('.lookbook__item[data-warn]').forEach(el => {
        el.removeAttribute('data-warn');
      });
    }

    toggleRow(index) {
      // No full re-render: images stay in place, no flicker on mobile.
      this.openRow = this.openRow === index ? -1 : index;
      this.clearWarns();
      this.dialog.querySelectorAll('.lookbook__item').forEach((el, i) => {
        const open = i === this.openRow;
        el.toggleAttribute('data-open', open);
        const rowBtn = el.querySelector('[data-lookbook-row]');
        if (rowBtn) rowBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      this.say('');
    }

    pickSize(index, variantId) {
      // No full re-render: only the selection state and the row label change.
      this.picked[index] = variantId;
      this.clearWarns();
      const item = this.dialog.querySelectorAll('.lookbook__item')[index];
      if (item) {
        item.querySelectorAll('[data-lookbook-size]').forEach(btn => {
          const id = parseInt(btn.getAttribute('data-lookbook-size').split('|')[1], 10);
          btn.setAttribute('aria-pressed', id === variantId ? 'true' : 'false');
        });
        const product = this.data.looks[this.current].products[index];
        const state = item.querySelector('.lookbook__state');
        if (state) state.textContent = this.variantLabel(product, variantId);
      }
      this.say('');
    }

    totals() {
      const look = this.data.looks[this.current];
      let sum = 0;
      let added = 0;
      look.products.forEach((p, i) => {
        sum += p.price;
        if (this.inCart[i] !== undefined) added += 1;
      });
      return { sum, added, count: look.products.length };
    }

    /* ---------- render ---------- */

    rowState(product, index) {
      if (this.inCart[index] !== undefined) {
        return `${this.icons.check || ''} ${this.esc(this.inCart[index])}`;
      }
      if (this.picked[index] !== undefined) {
        const v = product.variants.find(x => x.id === this.picked[index]);
        return v ? this.esc(v.label) : '';
      }
      if (!product.variants.some(v => v.available)) {
        return this.esc(this.strings.soldOut || '');
      }
      return '';
    }

    priceHTML(product) {
      let html = this.money(product.price);
      if (product.compareAt && product.compareAt > product.price) {
        html += `<span class="lookbook__was">${this.money(product.compareAt)}</span>`;
      }
      return html;
    }

    itemHTML(product, index) {
      const isOpen = this.openRow === index;
      const done = this.inCart[index] !== undefined;

      const sizes = product.variants
        .map(v => {
          return `<button type="button" class="lookbook__size" data-lookbook-size="${index}|${v.id}"
            aria-pressed="${this.picked[index] === v.id}"${v.available ? '' : ' disabled'}>${this.esc(v.label)}</button>`;
        })
        .join('');

      const addLabel = done
        ? `${this.strings.inCart || ''} · ${this.esc(this.inCart[index])}`
        : this.strings.addToCart || '';

      let cartNote = '';
      if (this.preview) {
        const pvV = this.cartPreviewVariant(product);
        const line = pvV && this.preview.byVariant[pvV.id];
        if (line && line.final < line.orig) {
          cartNote = `<span class="lookbook__cartline">${this.esc((this.strings.cartPrice || '[price]').replace('[price]', this.money(line.final)))}</span>`;
        }
      }

      const gal = product.gallery || [];
      const thumbs = gal.length
        ? `<div class="lookbook__gal">${gal
            .map(
              (g, gi) =>
                `<button type="button" class="lookbook__galthumb" data-lookbook-zoom="${index}|${gi}" aria-label="${this.esc((this.strings.image || '[n]').replace('[n]', gi + 1))}"><img src="${this.esc(g.thumb)}" alt="" width="48" height="62"></button>`
            )
            .join('')}</div>`
        : '';

      return `
        <div class="lookbook__item"${isOpen ? ' data-open' : ''}${this.warnRow === index ? ' data-warn' : ''}>
          <button type="button" class="lookbook__row" data-lookbook-row="${index}" aria-expanded="${isOpen}">
            ${
              product.image
                ? `<img class="lookbook__thumb" src="${this.esc(product.image)}" alt="" width="54" height="70">`
                : '<span class="lookbook__thumb"></span>'
            }
            <span class="lookbook__rowtext">
              <span class="lookbook__name">${this.esc(product.title)}</span>
              <span class="lookbook__price">${this.priceHTML(product)}${cartNote}</span>
            </span>
            <span class="lookbook__state">${this.rowState(product, index)}</span>
            <span class="lookbook__chev" aria-hidden="true">${this.icons.chevron || ''}</span>
          </button>
          <div class="lookbook__body"><div class="lookbook__bodyinner"><div class="lookbook__bodypad">
            ${thumbs}
            ${
              product.single
                ? ''
                : `<p class="lookbook__sizelabel">${this.esc(product.sizeName)}</p>
                   <div class="lookbook__sizes">${sizes}</div>`
            }
            <button type="button" class="btn btn--primary lookbook__add" data-lookbook-add="${index}"${done ? ' data-done' : ''}>${addLabel}</button>
            <a class="lookbook__link" href="${this.esc(product.url)}">${this.esc(this.strings.viewProduct || '')}</a>
          </div></div></div>
        </div>`;
    }

    ctaLabel() {
      const t = this.totals();
      const sum = this.previewFinal() !== null ? this.previewFinal() : t.sum;
      if (t.added === 0) return `${this.strings.addLook || ''} · ${this.money(sum)}`;
      if (t.added < t.count) {
        return (this.strings.addRemaining || '').replace('[count]', t.count - t.added);
      }
      return this.strings.viewCart || '';
    }

    render() {
      const look = this.data.looks[this.current];
      const t = this.totals();
      const pv = this.preview && this.preview.final < this.preview.orig ? this.preview : null;
      const images = look.images || [];
      const src = images[this.slide] || images[0] || '';

      const dots =
        images.length > 1
          ? `<div class="lookbook__dots">${images
              .map(
                (_, i) =>
                  `<button type="button" class="lookbook__dot" data-lookbook-slide="${i}" aria-current="${i === this.slide}" aria-label="${i + 1}"></button>`
              )
              .join('')}</div>`
          : '';

      const shareIcon = '<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M8 10V1.5M5.2 3.8L8 1l2.8 2.8"/><path d="M3 7.5v6h10v-6"/></svg>';

      this.dialog.innerHTML = `
        <div class="lookbook__stage">
          <button type="button" class="lookbook__stageclose" data-lookbook-close aria-label="${this.esc(this.strings.close || '')}">${this.icons.close || ''}</button>
          ${src ? `<img src="${this.esc(src)}" alt="${this.esc(look.title)}">` : ''}
          ${dots}
        </div>
        <div class="lookbook__panel">
          <div class="lookbook__panelhead">
            <div>
              <p class="lookbook__dlgno">${this.esc(this.strings.look)} ${this.esc(look.no)}${look.subtitle ? ` · ${this.esc(look.subtitle)}` : ''}</p>
              ${look.title ? `<h2 class="lookbook__dlgtitle">${this.esc(look.title)}</h2>` : ''}
            </div>
            <span class="lookbook__bag">${t.added ? `${t.added}/${t.count}` : ''}</span>
            <button type="button" class="lookbook__close" data-lookbook-share aria-label="${this.esc(this.strings.share || '')}">${shareIcon}</button>
            <button type="button" class="lookbook__close" data-lookbook-close aria-label="${this.esc(this.strings.close || '')}">${this.icons.close || ''}</button>
          </div>
          ${look.note ? `<p class="lookbook__note">${this.esc(look.note)}</p>` : ''}
          ${look.products.map((p, i) => this.itemHTML(p, i)).join('')}
          <div class="lookbook__foot">
            <div class="lookbook__total">
              <span class="lookbook__totallabel">${this.esc(this.strings.lookTotal || '')}</span>
              <span class="lookbook__totalvalue">${
                pv
                  ? `<span class="lookbook__was">${this.money(pv.orig)}</span> ${this.money(pv.final)}`
                  : this.money(t.sum)
              }</span>
            </div>
            ${pv ? `<p class="lookbook__cartnote">${this.esc(this.strings.cartNote || '')}</p>` : ''}
            <button type="button" class="btn btn--primary lookbook__cta" data-lookbook-cta>${this.ctaLabel()}</button>
            <p class="lookbook__msg" data-lookbook-msg role="status" aria-live="polite"></p>
            <div class="lookbook__nav">
              <button type="button" class="lookbook__navbtn" data-lookbook-step="-1"${this.current === 0 ? ' disabled' : ''}>
                <span aria-hidden="true">${this.icons.prev || ''}</span>${this.esc(this.strings.previous || '')}
              </button>
              <button type="button" class="lookbook__navbtn" data-lookbook-step="1"${this.current === this.data.looks.length - 1 ? ' disabled' : ''}>
                ${this.esc(this.strings.next || '')}<span aria-hidden="true">${this.icons.next || ''}</span>
              </button>
            </div>
          </div>
        </div>`;

      // Android safeguard: some WebViews cannot resolve the CSS variable on the
      // portaled modal; write the ratio inline on the stage to guarantee it.
      if (window.innerWidth <= 749) {
        const stage = this.dialog.querySelector('.lookbook__stage');
        if (stage) stage.style.aspectRatio = this.stageRatioRaw();
      }
    }

    /* ---------- open / close ---------- */

    open(index, push) {
      if (index < 0 || index >= this.data.looks.length) return;
      const fresh = this.current === -1;

      this.current = index;
      this.openRow = 0;
      this.warnRow = -1;
      this.slide = 0;
      this.picked = {};
      this.inCart = {};
      this.preview = null;

      if (fresh) {
        this.lastFocus = document.activeElement;
        document.querySelector('body').classList.add('overflow-hidden', 'lookbook-open');
        this.syncLayout();
        this.modal.hidden = false;
      }

      this.render();
      this.dialog.focus();
      this.loadCartPrices();

      if (typeof window.trapFocus === 'function') window.trapFocus(this.dialog);

      if (push !== false) {
        const url = new URL(window.location.href);
        url.searchParams.set('look', this.data.looks[index].no);
        history[fresh ? 'pushState' : 'replaceState']({ look: index }, '', url);
      }
    }

    close(push) {
      if (this.current === -1) return;
      this.current = -1;
      this.modal.hidden = true;
      this.dialog.innerHTML = '';
      document.querySelector('body').classList.remove('overflow-hidden', 'lookbook-open');

      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.lastFocus);
      else if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();

      if (push !== false) {
        const url = new URL(window.location.href);
        url.searchParams.delete('look');
        history.pushState({ look: null }, '', url);
      }
    }

    step(delta) {
      const next = this.current + delta;
      if (next < 0 || next >= this.data.looks.length) return;
      this.open(next);
    }

    openFromUrl() {
      const no = new URL(window.location.href).searchParams.get('look');
      if (!no) return;
      const index = this.data.looks.findIndex(l => l.no === no);
      if (index > -1) this.open(index, false);
    }

    onPopstate() {
      const no = new URL(window.location.href).searchParams.get('look');
      if (!no) {
        this.close(false);
        return;
      }
      const index = this.data.looks.findIndex(l => l.no === no);
      if (index > -1) this.open(index, false);
      else this.close(false);
    }

    /* ---------- cart ---------- */

    postItems(items, button, openDrawer) {
      // Symmetry: header + cart drawer sections are re-rendered from the add response.
      const sectionsToUpdate = ['page-header', 'cart-drawer']
        .map(sel => document.querySelector(sel))
        .filter(el => el && el.dataset.sectionId);
      const sectionIds = sectionsToUpdate.map(el => el.dataset.sectionId);

      const payload = { items };
      if (sectionIds.length) {
        payload.sections = sectionIds.join(',');
        payload.sections_url = window.location.pathname;
      }

      if (button) button.setAttribute('aria-busy', 'true');

      return fetch(this.data.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      })
        .then(response => response.json().then(data => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok || data.status || data.errors) {
            let error = typeof data.description === 'string' ? data.description : data.message;
            if (data.errors && typeof data.errors === 'object') {
              error = Object.values(data.errors).map(x => (Array.isArray(x) ? x.join(', ') : x)).join(' ');
            }
            throw new Error(error || this.strings.error);
          }

          const allSections =
            data.sections && sectionsToUpdate.every(el => data.sections[el.dataset.sectionId]);
          if (allSections) {
            sectionsToUpdate.forEach(el => {
              if (typeof el.updateFromCartChange === 'function') {
                el.updateFromCartChange(data.sections[el.dataset.sectionId]);
              }
            });
          } else {
            document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
          }

          if (openDrawer === true) this.afterAdd(button, data);
          return data;
        })
        .finally(() => {
          if (button) button.removeAttribute('aria-busy');
        });
    }

    afterAdd(button, data) {
      // Respects the theme's "after add to cart" setting (drawer / notification / page).
      const mode = this.themeSettings.afterAddToCart;
      const drawer = this.drawer;
      if (mode === 'page') {
        window.location.href = this.data.routes.cart;
        return;
      }
      if (drawer && typeof drawer.open === 'function' && (mode === 'drawer' || !mode)) {
        drawer.open(button || document.body);
        return;
      }
      const tpl = document.getElementById('AddedNotification');
      if (tpl && data) {
        const notification = tpl.content.firstElementChild.cloneNode(true);
        const first = Array.isArray(data.items) ? data.items[0] : data;
        notification.dataset.productTitle = (first && first.product_title) || '';
        (document.querySelector('.pageheader--sticky') || document.body).appendChild(notification);
      }
    }

    openCart() {
      const drawer = this.drawer;
      if (drawer && typeof drawer.open === 'function' && this.themeSettings.cartType === 'drawer') {
        drawer.open(document.body);
      } else {
        window.location.href = this.data.routes.cart;
      }
    }

    variantLabel(product, variantId) {
      const v = product.variants.find(x => x.id === variantId);
      return v ? v.label : '';
    }

    resolveVariant(product, index) {
      if (this.picked[index] !== undefined) return this.picked[index];
      if (product.variants.length === 1) return product.variants[0].id;
      return undefined;
    }

    addOne(index, button) {
      const product = this.data.looks[this.current].products[index];
      const variantId = this.resolveVariant(product, index);

      if (variantId === undefined) {
        this.warnRow = index;
        this.openRow = index;
        this.render();
        this.say(this.strings.selectSizeFirst, true);
        this.scrollToRow(index);
        return;
      }

      this.postItems([{ id: variantId, quantity: 1 }], button, false)
        .then(() => {
          this.inCart[index] = this.variantLabel(product, variantId);
          this.warnRow = -1;

          let next = -1;
          this.data.looks[this.current].products.forEach((_, j) => {
            if (next === -1 && this.inCart[j] === undefined) next = j;
          });
          this.openRow = next;

          this.render();
          this.say(next === -1 ? this.strings.lookAdded : this.strings.added);
        })
        .catch(err => this.say(err.message));
    }

    addRemaining(button) {
      const look = this.data.looks[this.current];
      const items = [];
      let missing = -1;
      let missingCount = 0;

      look.products.forEach((product, i) => {
        if (this.inCart[i] !== undefined) return;
        const variantId = this.resolveVariant(product, i);
        if (variantId === undefined) {
          if (missing === -1) missing = i;
          missingCount += 1;
          return;
        }
        items.push({ id: variantId, quantity: 1, index: i });
      });

      if (missing !== -1) {
        this.warnRow = missing;
        this.openRow = missing;
        this.render();
        this.say(
          missingCount > 1
            ? (this.strings.missingSizes || '').replace('[count]', missingCount)
            : this.strings.selectSize,
          true
        );
        this.scrollToRow(missing);
        return;
      }

      if (!items.length) return;

      this.postItems(
        items.map(x => ({ id: x.id, quantity: x.quantity })),
        button,
        true
      )
        .then(() => {
          items.forEach(x => {
            this.inCart[x.index] = this.variantLabel(look.products[x.index], x.id);
          });
          // Whole look is in the cart: close the popup, theme drawer takes over
          this.close();
        })
        .catch(err => this.say(err.message));
    }

    /* ---------- events ---------- */

    onClick(event) {
      const trigger = event.target.closest('[data-lookbook-open]');
      if (trigger) {
        this.open(parseInt(trigger.getAttribute('data-lookbook-open'), 10));
        return;
      }

      const scroller = event.target.closest('[data-lookbook-scroll]');
      if (scroller) {
        this.scrollTrack(parseInt(scroller.getAttribute('data-lookbook-scroll'), 10));
        return;
      }

      if (this.current === -1) return;

      if (event.target.closest('[data-lookbook-close]')) {
        this.close();
        return;
      }

      if (event.target.closest('[data-lookbook-share]')) {
        this.share();
        return;
      }

      const zoomStepBtn = event.target.closest('[data-lookbook-zoomstep]');
      if (zoomStepBtn) {
        this.stepZoom(parseInt(zoomStepBtn.getAttribute('data-lookbook-zoomstep'), 10));
        return;
      }

      if (event.target.closest('[data-lookbook-zoomclose]')) {
        this.closeZoom();
        return;
      }

      const zoomBg = event.target.closest('[data-lookbook-zoombox]');
      if (zoomBg) {
        if (event.target === zoomBg) this.closeZoom();
        return;
      }

      const zoomThumb = event.target.closest('[data-lookbook-zoom]');
      if (zoomThumb) {
        const [pi, gi] = zoomThumb.getAttribute('data-lookbook-zoom').split('|');
        this.openZoom(parseInt(pi, 10), parseInt(gi, 10));
        return;
      }

      const dot = event.target.closest('[data-lookbook-slide]');
      if (dot) {
        this.slide = parseInt(dot.getAttribute('data-lookbook-slide'), 10);
        this.render();
        return;
      }

      const row = event.target.closest('[data-lookbook-row]');
      if (row) {
        this.toggleRow(parseInt(row.getAttribute('data-lookbook-row'), 10));
        return;
      }

      const size = event.target.closest('[data-lookbook-size]');
      if (size) {
        const [i, id] = size.getAttribute('data-lookbook-size').split('|');
        this.pickSize(parseInt(i, 10), parseInt(id, 10));
        return;
      }

      const add = event.target.closest('[data-lookbook-add]');
      if (add) {
        this.addOne(parseInt(add.getAttribute('data-lookbook-add'), 10), add);
        return;
      }

      const cta = event.target.closest('[data-lookbook-cta]');
      if (cta) {
        const t = this.totals();
        if (t.added === t.count) {
          this.close();
          this.openCart();
          return;
        }
        this.addRemaining(cta);
        return;
      }

      const step = event.target.closest('[data-lookbook-step]');
      if (step) this.step(parseInt(step.getAttribute('data-lookbook-step'), 10));
    }

    scrollTrack(direction) {
      const viewport = this.querySelector('[data-lookbook-viewport]');
      if (!viewport) return;
      const cell = viewport.querySelector('.lbcar__cell');
      if (!cell) return;
      const gap = parseFloat(getComputedStyle(cell.parentElement).columnGap) || 8;
      viewport.scrollBy({ left: direction * (cell.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    onKeydown(event) {
      if (this.current === -1) return;
      if (this.zoomOpen()) {
        if (event.key === 'Escape') this.closeZoom();
        if (event.key === 'ArrowRight') this.stepZoom(1);
        if (event.key === 'ArrowLeft') this.stepZoom(-1);
        return;
      }
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowRight') this.step(1);
      if (event.key === 'ArrowLeft') this.step(-1);
    }
  }

  customElements.define('lookbook-section', LookbookSection);
}
